import type { CalendarEvent, CountryCode } from './types';
import { canonicalEventKey } from './event-canonical';

/** Returns the next event on or after referenceDate (default: today), or null */
export function getNextUpcomingEvent(events: CalendarEvent[], referenceDate?: string): CalendarEvent | null {
  const ref = referenceDate ?? new Date().toISOString().slice(0, 10);
  return events
    .filter(e => e.dateStart >= ref)
    .sort((a, b) => a.dateStart.localeCompare(b.dateStart))[0] || null;
}

/** Returns true for public_holiday or national_day event types */
export function isPublicHoliday(e: CalendarEvent): boolean {
  return e.type === 'public_holiday' || e.type === 'national_day';
}

/** Significance rank for sorting (lower = more significant). */
const SIGNIFICANCE_RANK: Record<string, number> = {
  critical: 0, highest: 1, high: 2, major: 3, notable: 4, medium: 5, low: 6,
};

/**
 * Returns upcoming events for the world-events ticker.
 * Filters to events on or after referenceDate, sorted by dateStart asc,
 * with a per-country diversity cap (max 3 per country in the candidate pool).
 * Ties on the same date prefer higher significance.
 */
export function getUpcomingTickerEvents(
  events: CalendarEvent[],
  referenceDate: string,
  count: number = 16
): CalendarEvent[] {
  const future = events
    .filter(e => e.visibility === 'global')
    .filter(e => e.dateStart >= referenceDate)
    .sort((a, b) => {
      const dateCmp = a.dateStart.localeCompare(b.dateStart);
      if (dateCmp !== 0) return dateCmp;
      return (SIGNIFICANCE_RANK[a.significance] ?? 6) - (SIGNIFICANCE_RANK[b.significance] ?? 6);
    });

  // Diversity cap: max 3 events per country in the first count*2 candidates
  const poolSize = count * 2;
  const pool: CalendarEvent[] = [];
  const countryCounts = new Map<string, number>();

  for (const e of future) {
    if (pool.length >= poolSize) break;
    const cc = countryCounts.get(e.country) ?? 0;
    if (cc >= 3) continue;
    countryCounts.set(e.country, cc + 1);
    pool.push(e);
  }

  return pool.slice(0, count);
}

/** A grouped ticker item — one entry per unique event (or event group + date). */
export interface TickerItem {
  /** Display title (from the first/representative event). */
  title: string;
  /** Localized title (from representative event's titleLocal field). */
  titleLocal?: string;
  /** ISO date string (YYYY-MM-DD). */
  dateStart: string;
  /** Event type for color-coding. */
  type: CalendarEvent['type'];
  /** Country codes contributing to this group. */
  countries: CountryCode[];
  /** Slug for the first event (used for linking). */
  eventId: string;
  /** eventGroup identifier, if grouped. */
  eventGroup?: string;
}

/**
 * Groups upcoming events by eventGroup+dateStart (or keeps ungrouped events solo).
 * Returns TickerItem[] sorted by dateStart, with grouped events collapsed into
 * a single item showing all contributing country flags.
 */
export function getGroupedTickerItems(
  events: CalendarEvent[],
  referenceDate: string,
  count: number = 16
): TickerItem[] {
  const future = events
    .filter(e => e.visibility === 'global')
    .filter(e => e.dateStart >= referenceDate)
    .sort((a, b) => {
      const dateCmp = a.dateStart.localeCompare(b.dateStart);
      if (dateCmp !== 0) return dateCmp;
      return (SIGNIFICANCE_RANK[a.significance] ?? 6) - (SIGNIFICANCE_RANK[b.significance] ?? 6);
    });

  // Group by canonical holiday key + dateStart. The ticker is a what's-on-now
  // strip, so unlike the hub lists it never widens the date: two countries
  // observing the same holiday on different days stay two entries.
  const grouped = new Map<string, { representative: CalendarEvent; countries: Set<CountryCode> }>();
  const insertionOrder: string[] = [];

  for (const e of future) {
    const key = `${canonicalEventKey(e)}:${e.dateStart}`;

    const existing = grouped.get(key);
    if (existing) {
      existing.countries.add(e.country);
    } else {
      grouped.set(key, { representative: e, countries: new Set([e.country]) });
      insertionOrder.push(key);
    }
  }

  const items: TickerItem[] = [];
  for (const key of insertionOrder) {
    if (items.length >= count) break;
    const entry = grouped.get(key);
    if (!entry) continue;
    const rep = entry.representative;
    items.push({
      title: rep.title,
      titleLocal: rep.titleLocal,
      dateStart: rep.dateStart,
      type: rep.type,
      countries: [...entry.countries],
      eventId: rep.id,
      eventGroup: rep.eventGroup,
    });
  }

  return items;
}

/**
 * Returns grouped ticker items filtered to a single country.
 * Uses the same grouping logic as getGroupedTickerItems but on pre-filtered events.
 */
export function getLocalTickerItems(
  events: CalendarEvent[],
  countryCode: CountryCode,
  referenceDate: string,
  count: number = 12
): TickerItem[] {
  const countryEvents = events.filter(e => e.country === countryCode);
  return getGroupedTickerItems(countryEvents, referenceDate, count);
}

/** Returns events that overlap with the given month (0-indexed) */
export function filterEventsByMonth(events: CalendarEvent[], year: number, month: number): CalendarEvent[] {
  const monthStartMs = new Date(year, month, 1).getTime();
  const monthEndMs = new Date(year, month + 1, 0).getTime();
  return events.filter(e => e.startMs <= monthEndMs && e.endMs >= monthStartMs);
}

/** Returns events active on a specific day (0-indexed month) */
export function filterEventsByDay(events: CalendarEvent[], year: number, month: number, day: number): CalendarEvent[] {
  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return events.filter(e => e.dateStart <= dateStr && e.dateEnd >= dateStr);
}

/** Returns top N public holidays from the event list */
export function getTopHolidays(events: CalendarEvent[], limit: number): CalendarEvent[] {
  return events.filter(isPublicHoliday).slice(0, limit);
}

/** Returns top N events that are public holidays, national days, or cultural festivals */
export function getTopEvents(events: CalendarEvent[], limit: number): CalendarEvent[] {
  return events.filter(
    (e) => e.type === 'public_holiday' || e.type === 'national_day' || e.type === 'cultural_festival'
  ).slice(0, limit);
}

/**
 * Groups events into a map keyed by Gregorian ISO date (YYYY-MM-DD).
 * Used by the multi-calendar grid where cell positions are derived from the
 * active calendar system but events stay in Gregorian; the grid renders the
 * cell's gregorianISO and looks up events from this map.
 */
export function groupEventsByGregorianISO(events: CalendarEvent[]): Record<string, CalendarEvent[]> {
  const map: Record<string, CalendarEvent[]> = {};
  for (const event of events) {
    // Walk the event's date range day-by-day in UTC; events span at most a few days.
    const start = new Date(event.startMs);
    const end = new Date(event.endMs);
    const cur = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate()));
    const last = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()));
    while (cur.getTime() <= last.getTime()) {
      const iso = `${cur.getUTCFullYear()}-${String(cur.getUTCMonth() + 1).padStart(2, '0')}-${String(cur.getUTCDate()).padStart(2, '0')}`;
      if (!map[iso]) map[iso] = [];
      map[iso].push(event);
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
  }
  return map;
}

/** Groups events into a map keyed by day number for a given month */
export function groupEventsByDay(events: CalendarEvent[], year: number, month: number): Record<number, CalendarEvent[]> {
  const map: Record<number, CalendarEvent[]> = {};
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthStartMs = new Date(year, month, 1).getTime();
  const monthEndMs = new Date(year, month, daysInMonth).getTime();

  // Pre-compute day timestamps: 31 Date calls instead of E×31
  const dayMs: number[] = new Array(daysInMonth + 1);
  for (let d = 1; d <= daysInMonth; d++) {
    dayMs[d] = new Date(year, month, d).getTime();
  }

  for (const event of events) {
    if (event.endMs < monthStartMs || event.startMs > monthEndMs) continue;

    for (let d = 1; d <= daysInMonth; d++) {
      if (dayMs[d] >= event.startMs && dayMs[d] <= event.endMs) {
        if (!map[d]) map[d] = [];
        map[d].push(event);
      }
    }
  }

  return map;
}
